import { supabase } from './supabase';

// ── Types for DSR Detail view ────────────────────────────────

export interface DSRDetailReport {
  id: string;
  tenant_id: string;
  project_id: string;
  location_id: string | null;
  report_date: string;
  shift_type: string;
  status: string;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  amended_by: string | null;
  amended_at: string | null;
  late_entry_reason: string | null;
  overall_risk_level: 'low' | 'medium' | 'high' | 'critical' | null;
  risk_score: number | null;
  risk_score_breakdown: {
    activity_points: number;
    safety_event_points: number;
    workforce_points: number;
    permit_points: number;
    total: number;
    signals: string[];
  } | null;
  risk_score_computed_at: string | null;
  risk_level_override: 'low' | 'medium' | 'high' | 'critical' | null;
  risk_level_override_reason: string | null;
  risk_level_override_by: string | null;
  risk_assessment_note: string | null;
  risk_recommendation_note: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  projects: { name: string } | null;
  users: { first_name: string; last_name: string; email: string } | null;
  dsr_activities_summaries: { overall_risk_level: string }[];
}

export interface DSRAnnotationRow {
  id: string;
  tenant_id: string;
  dsr_report_id: string;
  section_key: string | null;
  annotation_type: 'comment' | 'flag' | 'approval' | 'amendment';
  body: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  users: { first_name: string; last_name: string } | null;
  user_role?: string;
}

export interface PhotoAttachmentWithUrl {
  id: string;
  entity_type: string;
  entity_id: string;
  storage_path: string;
  file_name: string;
  signedUrl: string | null;
}

export interface DSRDetailData {
  report: DSRDetailReport;
  dailySetup: any | null;
  personnel: any[];
  activities: any[];
  equipment: any[];
  hseObservations: any[];
  permits: any[];
  meetings: any[];
  jsaEvaluations: any[];
  swaEvents: any[];
  safetyEvents: any[];
  forecastedActivities: any[];
  outstandingItems: any[];
  forecastingSummary: any | null;
  narrative: any | null;
  shiftLogs: any[];
  actionItems: any[];
  annotations: DSRAnnotationRow[];
  photoAttachments: PhotoAttachmentWithUrl[];
}

// ── Fetch helper (silently returns null/[] on missing tables) ─

async function fetchSection<T>(
  table: string,
  dsrId: string,
  columns: string,
  single?: boolean
): Promise<T | null> {
  try {
    let query = supabase
      .from(table)
      .select(columns)
      .eq('dsr_report_id', dsrId)
      .eq('is_deleted', false);

    if (single) {
      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return (data as T) ?? null;
    } else {
      const { data, error } = await query;
      if (error) throw error;
      return (data as T) ?? null;
    }
  } catch {
    return null;
  }
}

// ── Main fetch ───────────────────────────────────────────────

export async function getDSRDetail(dsrId: string): Promise<DSRDetailData> {
  // Fetch report with project + user joins
  const { data: report, error: reportErr } = await supabase
    .from('dsr_reports')
    .select(
      `*, projects ( name ),
       users!dsr_reports_created_by_fkey ( first_name, last_name, email ),
       dsr_activities_summaries ( overall_risk_level )`
    )
    .eq('id', dsrId)
    .single();

  if (reportErr || !report) {
    throw new Error(reportErr?.message ?? 'DSR report not found');
  }

  // Fetch all sections + annotations in parallel
  const [
    dailySetup,
    personnel,
    activities,
    equipment,
    hseObservations,
    permits,
    meetings,
    jsaEvaluations,
    swaEvents,
    safetyEvents,
    forecastedActivities,
    outstandingItems,
    forecastingSummary,
    narrative,
    shiftLogs,
    actionItems,
    annotationsRaw,
    photos,
  ] = await Promise.all([
    fetchSection('dsr_daily_setups', dsrId, '*', true),
    fetchSection<any[]>('dsr_personnel_summaries', dsrId, '*'),
    fetchSection<any[]>('dsr_activities', dsrId, '*'),
    fetchSection<any[]>('dsr_equipment', dsrId, '*'),
    fetchSection<any[]>('dsr_hse_observations', dsrId, '*'),
    fetchSection<any[]>('dsr_permit_entries', dsrId, '*'),
    fetchSection<any[]>('dsr_meeting_entries', dsrId, '*'),
    fetchSection<any[]>('dsr_jsa_evaluations', dsrId, '*'),
    fetchSection<any[]>('dsr_swa_events', dsrId, '*'),
    fetchSection<any[]>('dsr_safety_events', dsrId, '*'),
    fetchSection<any[]>('dsr_forecasted_activities', dsrId, '*'),
    fetchSection<any[]>('dsr_outstanding_items', dsrId, '*'),
    fetchSection('dsr_forecasting_summaries', dsrId, '*', true),
    fetchSection('dsr_narratives', dsrId, '*', true),
    fetchShiftLogs(dsrId),
    fetchSection<any[]>('dsr_action_items', dsrId, '*'),
    getAnnotations(dsrId),
    getPhotoAttachments(dsrId),
  ]);

  return {
    report: report as DSRDetailReport,
    dailySetup,
    personnel: personnel ?? [],
    activities: activities ?? [],
    equipment: equipment ?? [],
    hseObservations: hseObservations ?? [],
    permits: permits ?? [],
    meetings: meetings ?? [],
    jsaEvaluations: jsaEvaluations ?? [],
    swaEvents: swaEvents ?? [],
    safetyEvents: safetyEvents ?? [],
    forecastedActivities: forecastedActivities ?? [],
    outstandingItems: outstandingItems ?? [],
    forecastingSummary: forecastingSummary ?? null,
    narrative,
    shiftLogs: shiftLogs ?? [],
    actionItems: actionItems ?? [],
    annotations: annotationsRaw ?? [],
    photoAttachments: photos ?? [],
  };
}

// ── Shift Logs ──────────────────────────────────────────────

async function fetchShiftLogs(dsrId: string): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('dsr_shift_logs')
      .select('*')
      .eq('dsr_report_id', dsrId)
      .eq('is_deleted', false)
      .order('entry_time', { ascending: true });
    if (error) throw error;
    return data ?? [];
  } catch {
    return [];
  }
}

// ── Photo Attachments ───────────────────────────────────────

async function getPhotoAttachments(dsrId: string): Promise<PhotoAttachmentWithUrl[]> {
  const { data, error } = await supabase
    .from('photo_attachments')
    .select('id, entity_type, entity_id, storage_path, file_name')
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });

  if (error || !data) return [];

  // Generate signed URLs for all photos in parallel
  const withUrls = await Promise.all(
    data.map(async (row: any) => {
      const { data: urlData } = await supabase.storage
        .from('dsr-photos')
        .createSignedUrl(row.storage_path, 300);
      return {
        ...row,
        signedUrl: urlData?.signedUrl ?? null,
      } as PhotoAttachmentWithUrl;
    })
  );

  return withUrls;
}

export async function getPhotoSignedUrl(storagePath: string): Promise<string | null> {
  const { data } = await supabase.storage
    .from('dsr-photos')
    .createSignedUrl(storagePath, 300);
  return data?.signedUrl ?? null;
}

// ── Annotations ─────────────────────────────────────────────

export async function getAnnotations(dsrId: string): Promise<DSRAnnotationRow[]> {
  const { data, error } = await supabase
    .from('dsr_annotations')
    .select('*, users!dsr_annotations_created_by_fkey ( first_name, last_name )')
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });

  if (error) return [];
  return (data as DSRAnnotationRow[]) ?? [];
}

export async function addAnnotation(
  dsrId: string,
  payload: {
    tenant_id: string;
    annotation_type: 'comment' | 'flag' | 'approval' | 'amendment';
    body: string;
    section_key?: string | null;
    created_by: string;
  }
): Promise<DSRAnnotationRow> {
  const { data, error } = await supabase
    .from('dsr_annotations')
    .insert({
      dsr_report_id: dsrId,
      tenant_id: payload.tenant_id,
      annotation_type: payload.annotation_type,
      body: payload.body,
      section_key: payload.section_key ?? null,
      created_by: payload.created_by,
    })
    .select('*, users!dsr_annotations_created_by_fkey ( first_name, last_name )')
    .single();

  if (error) throw error;
  return data as DSRAnnotationRow;
}

export async function updateDSRReviewStatus(
  dsrId: string,
  status: 'reviewed' | 'flagged',
  reviewedBy: string
): Promise<void> {
  const { error } = await supabase
    .from('dsr_reports')
    .update({
      status,
      reviewed_by: reviewedBy,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', dsrId);

  if (error) throw error;
}
